// @ts-nocheck
import { useQuery, QueryHookOptions, ApolloError } from '@apollo/client';
import { useMemo } from 'react';

import {
  GET_OFFER_BY_ID_QUERY,
  GET_OFFER_BY_ID_BASE_QUERY,
  GET_OFFER_PROMOS_ONLY_QUERY,
  GET_OFFER_LINKS_ONLY_QUERY,
  GET_OFFER_PROMOS_AFFILIATES_QUERY,
  GET_OFFER_PROMOS_AFFILIATE_STATS_QUERY,
  GET_OFFER_PROMOS_STATS_QUERY,
  GET_OFFER_LINKS_AFFILIATES_QUERY,
  GET_OFFER_LINKS_AFFILIATE_STATS_QUERY,
  GET_OFFER_LINKS_STATS_QUERY,
} from '@affiliates/queries';
import { GetOfferById, GetOfferByIdVariables } from '@affiliates/queries/types/GetOfferById';
import { GetOfferByIdBase, GetOfferByIdBaseVariables } from '@affiliates/queries/types/GetOfferByIdBase';
import {
  GetOfferPromosAffiliates,
  GetOfferPromosAffiliatesVariables,
} from '@affiliates/queries/types/GetOfferPromosAffiliates';
import {
  GetOfferPromosAffiliateStats,
  GetOfferPromosAffiliateStatsVariables,
} from '@affiliates/queries/types/GetOfferPromosAffiliateStats';
import {
  GetOfferLinksAffiliates,
  GetOfferLinksAffiliatesVariables,
} from '@affiliates/queries/types/GetOfferLinksAffiliates';
import {
  GetOfferLinksAffiliateStats,
  GetOfferLinksAffiliateStatsVariables,
} from '@affiliates/queries/types/GetOfferLinksAffiliateStats';
import { GetOfferPromosStats, GetOfferPromosStatsVariables } from '../queries/types/GetOfferPromosStats';
import { GetOfferLinksStats, GetOfferLinksStatsVariables } from '../queries/types/GetOfferLinksStats';
import { GetOfferPromosOnly, GetOfferPromosOnlyVariables } from '../queries/types/GetOfferPromosOnly';
import { GetOfferLinksOnly, GetOfferLinksOnlyVariables } from '../queries/types/GetOfferLinksOnly';

interface OptimizedOptions extends QueryHookOptions<GetOfferById, GetOfferByIdVariables> {
  useLegacyQuery?: boolean;
}

interface OptimizedOfferData {
  data: GetOfferById | null;
  loading: boolean;
  error: ApolloError | null;
  refetch: (variables?: GetOfferByIdVariables) => void;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
}

export const useGetOfferByIdQuery = (options: OptimizedOptions = {}): OptimizedOfferData => {
  const {
    variables,
    useLegacyQuery,
    onCompleted,
    onError,
    defaultOptions,
    fetchPolicy,
    errorPolicy,
    notifyOnNetworkStatusChange,
    pollInterval,
    ...restOptions
  } = options;

  // Always call all hooks at the top level
  const legacyResult = useQuery<GetOfferById, GetOfferByIdVariables>(GET_OFFER_BY_ID_QUERY, {
    ...restOptions,
    onCompleted,
    onError,
    defaultOptions,
    fetchPolicy,
    errorPolicy,
    notifyOnNetworkStatusChange,
    pollInterval,
    skip: !useLegacyQuery,
  });

  // Base offer query
  const baseQuery = useQuery<GetOfferByIdBase, GetOfferByIdBaseVariables>(GET_OFFER_BY_ID_BASE_QUERY, {
    variables: variables?.id ? { id: variables.id } : undefined,
    onCompleted,
    onError,
    defaultOptions,
    notifyOnNetworkStatusChange,
    fetchPolicy,
    errorPolicy,
    pollInterval,
  });

  // Promos only query
  const promosOnlyQuery = useQuery<GetOfferPromosOnly, GetOfferPromosOnlyVariables>(GET_OFFER_PROMOS_ONLY_QUERY, {
    variables: { id: variables?.id ?? 0 },
    skip: !variables?.id || baseQuery.loading || !!baseQuery.error,
    fetchPolicy,
    errorPolicy,
    notifyOnNetworkStatusChange,
    pollInterval,
  });

  // Links only query
  const linksOnlyQuery = useQuery<GetOfferLinksOnly, GetOfferLinksOnlyVariables>(GET_OFFER_LINKS_ONLY_QUERY, {
    variables: { id: variables?.id ?? 0 },
    skip: !variables?.id || baseQuery.loading || !!baseQuery.error,
    fetchPolicy,
    errorPolicy,
    notifyOnNetworkStatusChange,
    pollInterval,
  });

  // Promo affiliates query
  const promoAffiliatesQuery = useQuery<GetOfferPromosAffiliates, GetOfferPromosAffiliatesVariables>(
    GET_OFFER_PROMOS_AFFILIATES_QUERY,
    {
      variables: { id: variables?.id ?? 0 },
      skip: !variables?.id || baseQuery.loading || !!baseQuery.error,
      fetchPolicy,
      errorPolicy,
      notifyOnNetworkStatusChange,
      pollInterval,
    },
  );

  // Promo affiliate stats query
  const promoAffiliateStatsQuery = useQuery<GetOfferPromosAffiliateStats, GetOfferPromosAffiliateStatsVariables>(
    GET_OFFER_PROMOS_AFFILIATE_STATS_QUERY,
    {
      variables: {
        id: variables?.id ?? 0,
        startDate: variables?.startDate,
        endDate: variables?.endDate,
      },
      skip: !variables?.id || baseQuery.loading || !!baseQuery.error,
      fetchPolicy,
      errorPolicy,
      notifyOnNetworkStatusChange,
      pollInterval,
    },
  );

  // Promo stats query
  const promoStatsQuery = useQuery<GetOfferPromosStats, GetOfferPromosStatsVariables>(GET_OFFER_PROMOS_STATS_QUERY, {
    variables: {
      id: variables?.id ?? 0,
      startDate: variables?.startDate,
      endDate: variables?.endDate,
      status: variables?.status,
    },
    skip: !variables?.id || baseQuery.loading || !!baseQuery.error,
    fetchPolicy,
    errorPolicy,
    notifyOnNetworkStatusChange,
    pollInterval,
  });

  // Link affiliates query
  const linkAffiliatesQuery = useQuery<GetOfferLinksAffiliates, GetOfferLinksAffiliatesVariables>(
    GET_OFFER_LINKS_AFFILIATES_QUERY,
    {
      variables: { id: variables?.id ?? 0 },
      skip: !variables?.id || baseQuery.loading || !!baseQuery.error,
      fetchPolicy,
      errorPolicy,
      notifyOnNetworkStatusChange,
      pollInterval,
    },
  );

  // Link affiliate stats query
  const linkAffiliateStatsQuery = useQuery<GetOfferLinksAffiliateStats, GetOfferLinksAffiliateStatsVariables>(
    GET_OFFER_LINKS_AFFILIATE_STATS_QUERY,
    {
      variables: {
        id: variables?.id ?? 0,
        startDate: variables?.startDate,
        endDate: variables?.endDate,
      },
      skip: !variables?.id || baseQuery.loading || !!baseQuery.error,
      fetchPolicy,
      errorPolicy,
      notifyOnNetworkStatusChange,
      pollInterval,
    },
  );

  // Link stats query
  const linkStatsQuery = useQuery<GetOfferLinksStats, GetOfferLinksStatsVariables>(GET_OFFER_LINKS_STATS_QUERY, {
    variables: {
      id: variables?.id ?? 0,
      startDate: variables?.startDate,
      endDate: variables?.endDate,
    },
    skip: !variables?.id || baseQuery.loading || !!baseQuery.error,
    fetchPolicy,
    errorPolicy,
    notifyOnNetworkStatusChange,
    pollInterval,
  });

  // Combine all queries into the final result
  const combinedData = useMemo(() => {
    if (!baseQuery.data?.offer) {
      return null;
    }

    const baseOffer = baseQuery.data.offer;

    // Create maps for promos data
    const promosMap = new Map();
    const promoAffiliatesMap = new Map();
    const promoAffiliateStatsMap = new Map();
    const promoStatsMap = new Map();

    // Process promos only data
    if (promosOnlyQuery.data?.offer) {
      promosOnlyQuery.data.offer.promos.forEach((promo) => {
        promosMap.set(promo.id, promo);
      });
    }

    // Process promo affiliates data
    if (promoAffiliatesQuery.data?.offer) {
      promoAffiliatesQuery.data.offer.promos.forEach((promo) => {
        promoAffiliatesMap.set(promo.id, {
          affiliates: promo.affiliates,
        });
      });
    }

    // Process promo affiliate stats data
    if (promoAffiliateStatsQuery.data?.offer) {
      promoAffiliateStatsQuery.data.offer.promos.forEach((promo) => {
        promoAffiliateStatsMap.set(promo.id, {
          affiliateStats: promo.affiliateStats,
        });
      });
    }

    // Process promo stats data
    if (promoStatsQuery.data?.offer) {
      promoStatsQuery.data.offer.promos.forEach((promo) => {
        promoStatsMap.set(promo.id, {
          stats: promo.stats,
        });
      });
    }

    // Create maps for links data
    const linksMap = new Map();
    const linkAffiliatesMap = new Map();
    const linkAffiliateStatsMap = new Map();
    const linkStatsMap = new Map();

    // Process links only data
    if (linksOnlyQuery.data?.offer) {
      linksOnlyQuery.data.offer.links.forEach((link) => {
        linksMap.set(link.id, link);
      });
    }

    // Process link affiliates data
    if (linkAffiliatesQuery.data?.offer) {
      linkAffiliatesQuery.data.offer.links.forEach((link) => {
        linkAffiliatesMap.set(link.id, {
          affiliates: link.affiliates,
        });
      });
    }

    // Process link affiliate stats data
    if (linkAffiliateStatsQuery.data?.offer) {
      linkAffiliateStatsQuery.data.offer.links.forEach((link) => {
        linkAffiliateStatsMap.set(link.id, {
          canEditConversionType: link.canEditConversionType,
          affiliateStats: link.affiliateStats,
        });
      });
    }

    // Process link stats data
    if (linkStatsQuery.data?.offer) {
      linkStatsQuery.data.offer.links.forEach((link) => {
        linkStatsMap.set(link.id, {
          stats: link.stats,
        });
      });
    }

    // Get all promo IDs and link IDs from the maps
    const promoIds = Array.from(promosMap.keys());
    const linkIds = Array.from(linksMap.keys());

    // Merge the data
    const mergedOffer: GetOfferById['offer'] = {
      ...baseOffer,
      links: linkIds.map((linkId) => ({
        ...linksMap.get(linkId),
        ...linkAffiliateStatsMap.get(linkId),
        ...linkStatsMap.get(linkId),
        ...linkAffiliatesMap.get(linkId),
      })),
      promos: promoIds.map((promoId) => ({
        ...promosMap.get(promoId),
        ...promoAffiliateStatsMap.get(promoId),
        ...promoStatsMap.get(promoId),
        ...promoAffiliatesMap.get(promoId),
      })),
    };

    return { offer: mergedOffer };
  }, [
    baseQuery.data,
    promosOnlyQuery.data,
    linksOnlyQuery.data,
    promoAffiliatesQuery.data,
    promoAffiliateStatsQuery.data,
    promoStatsQuery.data,
    linkAffiliatesQuery.data,
    linkAffiliateStatsQuery.data,
    linkStatsQuery.data,
  ]);

  // Only branch on the result after all hooks have been called
  if (useLegacyQuery) {
    return {
      data: legacyResult.data || null,
      loading: legacyResult.loading,
      error: legacyResult.error || null,
      refetch: legacyResult.refetch,
      startPolling: legacyResult.startPolling,
      stopPolling: legacyResult.stopPolling,
    };
  }

  // Determine loading and error states
  const isLoading =
    baseQuery.loading ||
    promosOnlyQuery.loading ||
    linksOnlyQuery.loading ||
    promoAffiliatesQuery.loading ||
    promoAffiliateStatsQuery.loading ||
    promoStatsQuery.loading ||
    linkAffiliatesQuery.loading ||
    linkAffiliateStatsQuery.loading ||
    linkStatsQuery.loading;

  const error =
    baseQuery.error ||
    promosOnlyQuery.error ||
    linksOnlyQuery.error ||
    promoAffiliatesQuery.error ||
    promoAffiliateStatsQuery.error ||
    promoStatsQuery.error ||
    linkAffiliatesQuery.error ||
    linkAffiliateStatsQuery.error ||
    linkStatsQuery.error;

  // Refetch function that refetches all queries
  const refetch = (variables?: GetOfferByIdVariables) => {
    baseQuery.refetch(variables);
    promosOnlyQuery.refetch(variables);
    linksOnlyQuery.refetch(variables);
    promoAffiliatesQuery.refetch(variables);
    promoAffiliateStatsQuery.refetch(variables);
    promoStatsQuery.refetch(variables);
    linkAffiliatesQuery.refetch(variables);
    linkAffiliateStatsQuery.refetch(variables);
    linkStatsQuery.refetch(variables);
  };

  // Polling functions that start/stop polling on both promo and link affiliates queries
  const startPolling = (interval: number) => {
    promoAffiliatesQuery.startPolling(interval);
    linkAffiliatesQuery.startPolling(interval);
    linkAffiliateStatsQuery.startPolling(interval);
    promoAffiliateStatsQuery.startPolling(interval);
  };

  const stopPolling = () => {
    promoAffiliatesQuery.stopPolling();
    linkAffiliatesQuery.stopPolling();
    linkAffiliateStatsQuery.stopPolling();
    promoAffiliateStatsQuery.stopPolling();
  };

  return {
    data: combinedData,
    loading: isLoading,
    error,
    refetch,
    startPolling,
    stopPolling,
  };
};
